import React, { useState } from 'react';
import axios from 'axios';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './LeadCaptureModal.css';

// A raw dial-code-prefixed digit string from react-phone-input-2 (e.g.
// "923001234567") becomes E.164 ("+923001234567") by just prepending
// "+" -- the library always includes the dial code at the start of its
// value, confirmed by its own docs' "phone without dialCode" example
// (which strips it back off via data.dialCode.length).
const toE164 = (raw) => (raw ? `+${raw}` : undefined);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Client-side mirror of the server's actual required-field rule, purely
// for immediate inline feedback -- the server request/response and what
// it accepts are unchanged; this never overrides a server rejection.
const validate = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Please enter your name.';
  if (!form.email.trim()) errors.email = 'Please enter your email.';
  else if (!EMAIL_RE.test(form.email.trim())) errors.email = 'Please enter a valid email address.';
  return errors;
};

// Maps a server rejection back onto the field(s) it concerns. The phone
// endpoint's "Invalid phone number" message doesn't say which of
// phone/whatsapp caused it, so both get flagged rather than guessing.
const errorsFromResponse = (err) => {
  const data = err.response && err.response.data;
  if (data && Array.isArray(data.errors)) {
    const fieldErrors = {};
    data.errors.forEach((e) => {
      if (e.param) fieldErrors[e.param] = e.msg;
    });
    return fieldErrors;
  }
  if (data && data.msg === 'Invalid phone number') {
    return { phone: data.msg, whatsapp: data.msg };
  }
  return { general: (data && data.msg) || 'Something went wrong. Please try again.' };
};

const LeadCaptureModal = ({ lengthId, ownerName, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    message: '',
    companyWebsite: '', // honeypot -- never shown to a real visitor
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState('form'); // 'form' | 'submitting' | 'success'

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();

    const clientErrors = validate(form);
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      return;
    }

    setFieldErrors({});
    setStatus('submitting');
    try {
      await axios.post(`/api/users/leads/${lengthId}`, {
        name: form.name,
        email: form.email,
        phone: toE164(form.phone),
        whatsapp: toE164(form.whatsapp),
        message: form.message || undefined,
        companyWebsite: form.companyWebsite,
      });
      setStatus('success');
    } catch (err) {
      setStatus('form');
      setFieldErrors(errorsFromResponse(err));
    }
  };

  const inputClass = (field) => `lead-input${fieldErrors[field] ? ' lead-input--error' : ''}`;

  return (
    <div className="lead-modal-backdrop" onClick={onClose}>
      <div className="lead-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lead-modal__header">
          <h4 className="lead-modal__title">Share your details</h4>
          <span className="lead-modal__close" onClick={onClose} aria-label="Close">
            &times;
          </span>
        </div>

        {status === 'success' ? (
          <div className="lead-modal__success">
            <h4>Thanks{ownerName ? `, sent to ${ownerName}` : ''}!</h4>
            <p>Your details have been shared.</p>
            <button type="button" className="lead-submit" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <p className="lead-modal__disclosure">
              Your details go directly to the owner of this card, not to any
              third party.
            </p>

            {/* Honeypot -- off-screen, never focusable, never announced.
                A real visitor never sees or fills this. */}
            <input
              type="text"
              name="companyWebsite"
              value={form.companyWebsite}
              onChange={onChange}
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-5000px', width: '1px', height: '1px' }}
            />

            {fieldErrors.general && (
              <p className="lead-field__general-error">{fieldErrors.general}</p>
            )}

            <div className="lead-field">
              <input
                type="text"
                name="name"
                placeholder="Full Name"
                className={inputClass('name')}
                value={form.name}
                onChange={onChange}
              />
              {fieldErrors.name && <p className="lead-field__error">{fieldErrors.name}</p>}
            </div>

            <div className="lead-field">
              <input
                type="email"
                name="email"
                placeholder="Email"
                className={inputClass('email')}
                value={form.email}
                onChange={onChange}
              />
              {fieldErrors.email && <p className="lead-field__error">{fieldErrors.email}</p>}
            </div>

            <div className="lead-field lead-phone">
              <PhoneInput
                country="pk"
                value={form.phone}
                onChange={(phone) => setForm((prev) => ({ ...prev, phone }))}
                inputProps={{ name: 'phone', placeholder: 'Phone (optional)' }}
              />
              {fieldErrors.phone && <p className="lead-field__error">{fieldErrors.phone}</p>}
            </div>

            <div className="lead-field lead-phone">
              <PhoneInput
                country="pk"
                value={form.whatsapp}
                onChange={(whatsapp) => setForm((prev) => ({ ...prev, whatsapp }))}
                inputProps={{ name: 'whatsapp', placeholder: 'WhatsApp (optional)' }}
              />
              {fieldErrors.whatsapp && <p className="lead-field__error">{fieldErrors.whatsapp}</p>}
            </div>

            <div className="lead-field">
              <textarea
                name="message"
                placeholder="Message (optional)"
                className="lead-textarea"
                value={form.message}
                onChange={onChange}
              />
            </div>

            <button type="submit" className="lead-submit" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Sending…' : 'Share your details'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LeadCaptureModal;
